import React, { Component } from 'react';
import { Button, Input } from 'reactstrap';

import JsonViewer from '../../JsonViewer';
import { parseJsonValue } from '../fieldUtils';

// json-typed fields (e.g. KADUNA_GATEWAYS) hold an object or an array. The server
// also accepts a JSON string and parses it, but a malformed one 400s — so raw
// mode validates before the value ever leaves the form.
//
// The tree editor cannot represent a value that is not an object/array, so a
// value that fails to parse falls back to raw text automatically.
class JsonField extends Component {
  state = {
    // null = follow the value; otherwise an explicit user choice.
    mode: null,
    // Raw text is only tracked while raw mode is open. It is seeded from the
    // current value on entry, so a re-render from a save re-read reseeds it.
    raw: null,
    rawError: null,
  };

  parsed() {
    return parseJsonValue(this.props.value);
  }

  isTreeable() {
    const parsed = this.parsed();
    return (
      parsed.ok && parsed.value !== null && typeof parsed.value === 'object'
    );
  }

  currentMode() {
    if (!this.isTreeable()) return 'raw';
    return this.state.mode || 'tree';
  }

  toText() {
    const parsed = this.parsed();
    if (!parsed.ok) return String(this.props.value);
    if (parsed.value == null) return '';
    return JSON.stringify(parsed.value, null, 2);
  }

  switchTo = (mode) => {
    this.setState({
      mode,
      raw: mode === 'raw' ? this.toText() : null,
      rawError: null,
    });
  };

  handleRawChange = (e) => {
    if (this.props.disabled) return;

    const raw = e.target.value;
    const parsed = parseJsonValue(raw);

    this.setState({ raw, rawError: parsed.ok ? null : parsed.message });
    // Push the value up as parsed JSON when it is valid, so the batch validator
    // and the request body both see structure rather than a string. While it is
    // malformed we hold it locally — the field keeps its last good value.
    if (parsed.ok) this.props.onChange(this.props.field.key, parsed.value);
  };

  renderTree() {
    const { field, onChange, disabled } = this.props;
    const parsed = this.parsed();
    const apply = ({ updated_src }) => onChange(field.key, updated_src);
    // react-json-view decides what is editable from the presence of these
    // handlers, so withholding them is what makes the tree read-only.
    const editHandlers = disabled
      ? {}
      : { onEdit: apply, onAdd: apply, onDelete: apply };

    return (
      <JsonViewer
        src={parsed.value == null ? {} : parsed.value}
        name={field.key}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard={false}
        iconStyle={'circle'}
        {...editHandlers}
      />
    );
  }

  renderRaw() {
    const { field, disabled } = this.props;
    const { raw, rawError } = this.state;
    const text = raw == null ? this.toText() : raw;

    return (
      <div>
        <Input
          type="textarea"
          name={field.key}
          value={text}
          disabled={disabled}
          onChange={this.handleRawChange}
          rows={Math.min(16, Math.max(4, text.split('\n').length + 1))}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
          className={rawError ? 'border-danger' : undefined}
        />
        {rawError && (
          <small
            className="text-danger"
            style={{ display: 'block', marginTop: '6px' }}
          >
            Invalid JSON: {rawError}
          </small>
        )}
      </div>
    );
  }

  render() {
    const mode = this.currentMode();
    const treeable = this.isTreeable();
    // Switching representation is not a mutation, so it stays available to a
    // read-only viewer — only the wording changes.
    const verb = this.props.disabled ? 'View' : 'Edit';

    return (
      <div>
        {mode === 'tree' ? this.renderTree() : this.renderRaw()}
        <div style={{ marginTop: '6px' }}>
          <Button
            color="link"
            size="sm"
            className="p-0"
            disabled={!treeable}
            onClick={() => this.switchTo(mode === 'tree' ? 'raw' : 'tree')}
          >
            {mode === 'tree' ? `${verb} as JSON text` : `${verb} as tree`}
          </Button>
          {!treeable && (
            <small className="text-muted ml-2">
              Tree editing needs a valid JSON object or array.
            </small>
          )}
        </div>
      </div>
    );
  }
}

export default JsonField;
